import axios from "axios";
import jwt_decode from "jwt-decode";
import {
	AccountInfo,
	InteractionRequiredAuthError,
	RedirectRequest,
	SilentRequest,
} from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import { useClientIdentificator } from "hooks/useClientIdentificator";
import { StorageKey, LoginFormKey } from "domain/Utils";
import useRememberMe from "hooks/useRememberMe";
import Environment from "utils/environment";
import { loginRequest } from "auth/msalConfig";

const PRELOGIN_EMAIL_KEY = "prelogin_email";

const authSession = {
	setPreLoginEmail(email: string) {
		sessionStorage.setItem(PRELOGIN_EMAIL_KEY, email);
	},
	getPreLoginEmail(): string {
		return sessionStorage.getItem(PRELOGIN_EMAIL_KEY) || "";
	},
	clearPreLoginEmail() {
		sessionStorage.removeItem(PRELOGIN_EMAIL_KEY);
	},
};

const buildLoginRequest = (email?: string): RedirectRequest => ({
	...loginRequest,
	loginHint: email || undefined,
});

const buildMsalTokenRequest = (account: AccountInfo): SilentRequest => ({
	account,
	scopes: loginRequest.scopes,
});

export const useIdentityAuthentication = () => {
	const { instance, accounts } = useMsal();
	const { client, environment } = useClientIdentificator();
	const { storage } = useRememberMe();

	const getActiveAccount = (): AccountInfo | null => {
		const activeAccount = instance.getActiveAccount();
		if (activeAccount) return activeAccount;

		if (accounts.length > 0) {
			instance.setActiveAccount(accounts[0]);
			return accounts[0];
		}

		return null;
	};

	const startMicrosoftLogin = async (email: string): Promise<void> => {
		authSession.setPreLoginEmail(email);
		await instance.loginRedirect(buildLoginRequest(email));
	};

	const getMsalIdToken = async (): Promise<string> => {
		const account = getActiveAccount();
		if (!account) return "";

		try {
			const response = await instance.acquireTokenSilent(
				buildMsalTokenRequest(account)
			);

			return response.idToken || "";
		} catch (error) {
			if (error instanceof InteractionRequiredAuthError) {
				await instance.acquireTokenRedirect(buildMsalTokenRequest(account));
			}

			return "";
		}
	};

	const loginWithPassword = async (
		email: string,
		password: string
	): Promise<any> => {
		const formData = new URLSearchParams();

		formData.append(LoginFormKey.GrantType, Environment.AuthorityGrantType);
		formData.append(LoginFormKey.Scope, Environment.AuthorityScope);
		formData.append(LoginFormKey.Username, email);
		formData.append(LoginFormKey.Password, password);
		formData.append(LoginFormKey.Tenant, client);
		formData.append(LoginFormKey.Environment, environment);
		formData.append(LoginFormKey.ClientId, Environment.AuthorityClientId);
		formData.append(LoginFormKey.ClientSecret, Environment.AuthoritySecret);

		const auth = await axios.post(Environment.LoginURL, formData.toString(), {
			headers: {
				"Content-Type": "application/x-www-form-urlencoded",
			},
		});

		saveCredentials(auth);
		return auth;
	};

	const loginWithIdentityToken = async (
		email: string,
		temporaryToken: string
	): Promise<any> => {
		const formData = new URLSearchParams();

		formData.append(LoginFormKey.GrantType, Environment.AuthorityGrantType);
		formData.append(LoginFormKey.Scope, Environment.AuthorityScope);
		formData.append(LoginFormKey.Username, email);
		formData.append(LoginFormKey.Password, temporaryToken);
		formData.append(LoginFormKey.Tenant, client);
		formData.append(LoginFormKey.Environment, environment);
		formData.append(LoginFormKey.ClientId, Environment.AuthorityClientId);
		formData.append(LoginFormKey.ClientSecret, Environment.AuthoritySecret);
		formData.append("origin", "SSO");

		const auth = await axios.post(Environment.LoginURL, formData.toString(), {
			headers: {
				"Content-Type": "application/x-www-form-urlencoded",
			},
		});

		saveCredentials(auth);
		return auth;
	};

	const completeSSOLogin = async (): Promise<boolean> => {
		if (userIsLogged()) return true;

		const account = getActiveAccount();
		if (!account) return false;

		const email =
			authSession.getPreLoginEmail() ||
			account.username ||
			account.idTokenClaims?.preferred_username ||
			"";

		if (!email) return false;

		const msalIdToken = await getMsalIdToken();
		if (!msalIdToken) return false;

		const temporaryToken = await axios
			.get(`${Environment.API}Account/token`, {
				headers: {
					Authorization: `Bearer ${msalIdToken}`,
					"X-Tenant": client,
					...(environment ? { "X-Environment": environment } : {}),
				},
			})
			.then((response) => response.data as string);

		if (!temporaryToken) return false;

		await loginWithIdentityToken(email, temporaryToken);
		authSession.clearPreLoginEmail();

		return true;
	};

	const logout = async (): Promise<void> => {
		authSession.clearPreLoginEmail();

		storage().removeItem(StorageKey.Identity);
		storage().removeItem(StorageKey.SBDate);
		storage().removeItem(StorageKey.SBFilters);

		instance.setActiveAccount(null);

		const account = accounts[0] || instance.getActiveAccount();
		if (account) {
			await instance.clearCache({ account });
		}
	};

	const getAccount = (): any => {
		try {
			const token = getCredentials().data.access_token;
			return jwt_decode(token);
		} catch (e) {
			return null;
		}
	};

	const getToken = (): string => {
		try {
			return getCredentials().data.access_token;
		} catch (e) {
			return "";
		}
	};

	const userIsLogged = (): boolean => {
		return !!getAccount();
	};

	const saveCredentials = (auth: any) => {
		storage().setItem(StorageKey.Identity, JSON.stringify(auth));
	};

	const getCredentials = (): any => {
		try {
			const identity = storage().getItem(StorageKey.Identity);
			return JSON.parse(identity ?? "");
		} catch (e) {
			return null;
		}
	};

	return {
		startMicrosoftLogin,
		completeSSOLogin,
		loginWithIdentityToken,
		loginWithPassword,
		getMsalIdToken,
		getToken,
		getAccount,
		userIsLogged,
		logout,
		getActiveAccount,
		authSession,
	};
};